import { FormEvent, useEffect, useMemo, useState } from 'react'
import { ArrowRight, Mail, Users } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

type OpenInvitePreview = {
  organisation_name: string
  team_name: string
  expires_at: string
  is_available: boolean
}

type BatchPreview = {
  organisation_name: string
  team_name: string
  invitation_role: string
  expires_at: string
  invited_count: number
  remaining_count: number
  is_available: boolean
}

type LegacyPreview = {
  organisation_name: string
  team_name: string
  invited_email: string
  invitation_role: string
  expires_at: string
  is_available: boolean
}

type InvitationKind = 'open' | 'batch' | 'legacy'

function validEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

export function Join() {
  const location = useLocation()
  const navigate = useNavigate()
  const token = new URLSearchParams(location.search).get('token') || ''
  const storageKey = useMemo(() => `mail-percent-invite-email:${token}`, [token])

  const [kind, setKind] = useState<InvitationKind>('open')
  const [openPreview, setOpenPreview] = useState<OpenInvitePreview | null>(null)
  const [batchPreview, setBatchPreview] = useState<BatchPreview | null>(null)
  const [legacyPreview, setLegacyPreview] = useState<LegacyPreview | null>(null)
  const [signedIn, setSignedIn] = useState(false)
  const [signedInEmail, setSignedInEmail] = useState('')
  const [signedInRole, setSignedInRole] = useState('')
  const [workEmail, setWorkEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    async function prepare() {
      if (!supabase || !token) {
        setError('This invitation link is incomplete.')
        setLoading(false)
        return
      }

      const openResult = await supabase.rpc('get_employee_invite_link_preview', { p_token: token })
      const openInvite = openResult.data?.[0] as OpenInvitePreview | undefined

      if (!openResult.error && openInvite) {
        setKind('open')
        setOpenPreview(openInvite)
      } else {
        const batchResult = await supabase.rpc('get_team_invitation_batch_preview', { p_token: token })
        const batch = batchResult.data?.[0] as BatchPreview | undefined

        if (!batchResult.error && batch) {
          setKind('batch')
          setBatchPreview(batch)
        } else {
          const legacyResult = await supabase.rpc('get_team_invitation_preview', { p_token: token })

          if (legacyResult.error) {
            setError(openResult.error?.message || legacyResult.error.message)
            setLoading(false)
            return
          }

          const legacy = legacyResult.data?.[0] as LegacyPreview | undefined
          if (!legacy) {
            setError('This invitation could not be found.')
            setLoading(false)
            return
          }

          setKind('legacy')
          setLegacyPreview(legacy)
        }
      }

      const { data: { user } } = await supabase.auth.getUser()
      const accountEmail = user?.email ?? ''
      const pendingEmail = window.sessionStorage.getItem(storageKey) || ''

      let accountRole = ''
      if (user) {
        const { data: membership } = await supabase
          .from('memberships')
          .select('role')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle()
        accountRole = String(membership?.role ?? '')
      }

      const employeeAccount = accountRole === 'member' || user?.user_metadata?.account_mode === 'employee'

      setSignedIn(Boolean(user))
      setSignedInEmail(accountEmail)
      setSignedInRole(accountRole)
      setWorkEmail(pendingEmail || (employeeAccount ? accountEmail : ''))
      setFullName(employeeAccount ? String(user?.user_metadata?.full_name ?? '') : '')
      setLoading(false)
    }

    prepare()
  }, [storageKey, token])

  async function continueWithAccount(destination: 'signup' | 'login') {
    if (!supabase) {
      setError('Mail Percent is not configured.')
      return
    }

    const cleanEmail = workEmail.trim().toLowerCase()
    const currentEmail = signedInEmail.trim().toLowerCase()
    setError('')

    if (!validEmail(cleanEmail)) {
      setError('Enter a valid work email address first.')
      return
    }

    if (signedIn && signedInRole && signedInRole !== 'member' && cleanEmail === currentEmail) {
      setError('This invitation is for an employee. Enter the employee’s own work email, not the manager account currently signed in.')
      return
    }

    window.sessionStorage.setItem(storageKey, cleanEmail)

    if (signedIn && (currentEmail !== cleanEmail || (signedInRole && signedInRole !== 'member'))) {
      const { error: signOutError } = await supabase.auth.signOut()
      if (signOutError) {
        setError('Mail Percent could not switch accounts. Sign out, reopen the invitation link, and try again.')
        return
      }
      setSignedIn(false)
      setSignedInEmail('')
      setSignedInRole('')
    }

    const returnTo = `/join?token=${encodeURIComponent(token)}`
    const params = new URLSearchParams({
      email: cleanEmail,
      returnTo,
      invite: token
    })

    if (destination === 'signup') params.set('mode', 'employee')
    else params.set('employee', '1')

    navigate(`/${destination}?${params.toString()}`)
  }

  async function accept(event: FormEvent) {
    event.preventDefault()
    if (!supabase || !token) return

    setBusy(true)
    setError('')

    let result
    if (kind === 'open') {
      const cleanEmail = workEmail.trim().toLowerCase()
      if (!validEmail(cleanEmail)) {
        setBusy(false)
        setError('Enter a valid work email address.')
        return
      }

      result = await supabase.rpc('accept_employee_invite_link', {
        p_token: token,
        p_email: cleanEmail,
        p_display_name: fullName.trim() || null
      })
    } else if (kind === 'batch') {
      result = await supabase.rpc('accept_team_invitation_batch', {
        p_token: token,
        p_display_name: fullName.trim() || null
      })
    } else {
      result = await supabase.rpc('accept_team_invitation', {
        p_token: token,
        p_display_name: fullName.trim() || null
      })
    }

    setBusy(false)

    if (result.error) {
      setError(result.error.message)
      return
    }

    window.sessionStorage.removeItem(storageKey)
    navigate('/employee?joined=1', { replace: true })
  }

  if (loading) {
    return <div className="auth-loading"><span className="blue-spinner"/></div>
  }

  const organisationName = openPreview?.organisation_name ?? batchPreview?.organisation_name ?? legacyPreview?.organisation_name ?? ''
  const teamName = openPreview?.team_name ?? batchPreview?.team_name ?? legacyPreview?.team_name ?? ''
  const role = batchPreview?.invitation_role ?? legacyPreview?.invitation_role ?? 'member'
  const available = openPreview?.is_available ?? batchPreview?.is_available ?? legacyPreview?.is_available ?? false
  const cleanWorkEmail = workEmail.trim().toLowerCase()
  const currentAccountEmail = signedInEmail.trim().toLowerCase()
  const currentAccountIsManager = Boolean(signedInRole && signedInRole !== 'member')
  const signedInEmployeeMatches = Boolean(signedIn && cleanWorkEmail && currentAccountEmail === cleanWorkEmail && !currentAccountIsManager)

  return (
    <div className="auth-page employee-invite-page">
      <div className="auth-brand">
        <img className="mail-percent-logo app-logo" src="/images/brand/mail-percent-logo.png" alt="Mail Percent" />
      </div>

      <main className="auth-card auth-card-wide employee-invite-card">
        <div className="auth-icon"><Users size={22}/></div>
        <span className="eyebrow">Employee invitation</span>

        {organisationName ? (
          <>
            <h1>Join {organisationName}</h1>
            <p>You are joining the <strong>{teamName}</strong> team in that organisation's Mail Percent employee workspace.</p>

            {!available ? (
              <p className="auth-error">This invitation is no longer available. Ask your manager for a new link.</p>
            ) : kind === 'open' && !signedInEmployeeMatches ? (
              <div className="auth-form auth-form-spacious">
                <div className="employee-invite-email-intro">
                  <Mail size={19}/>
                  <div>
                    <strong>Enter your own work email</strong>
                    <span>This must be the employee’s email — not the manager’s account and not a shared team inbox.</span>
                  </div>
                </div>

                {signedIn && (currentAccountIsManager || currentAccountEmail !== cleanWorkEmail) && (
                  <div className="auth-notice">
                    <strong>Another Mail Percent account is currently signed in.</strong>
                    <span>Signed in as {signedInEmail}. Continuing with the employee email below will switch away from this account.</span>
                  </div>
                )}

                <label>
                  Employee work email
                  <input
                    className="search-input"
                    type="email"
                    value={workEmail}
                    onChange={event => setWorkEmail(event.target.value)}
                    placeholder="employee@company.com"
                    autoComplete="email"
                    required
                  />
                </label>

                {error && <p className="auth-error">{error}</p>}

                <button className="button primary large" type="button" onClick={() => continueWithAccount('signup')}>
                  Create employee account <ArrowRight size={17}/>
                </button>
                <button className="button ghost large" type="button" onClick={() => continueWithAccount('login')}>
                  I already have an employee account
                </button>
              </div>
            ) : signedIn ? (
              <form className="auth-form auth-form-spacious" onSubmit={accept}>
                {kind === 'open' ? (
                  <>
                    <label>
                      Employee work email
                      <input
                        className="search-input"
                        type="email"
                        value={workEmail}
                        readOnly
                        required
                        autoComplete="email"
                      />
                    </label>
                    <small className="muted">Signed in as <strong>{signedInEmail}</strong>. This is the employee account that will join {organisationName}.</small>
                  </>
                ) : kind === 'batch' ? (
                  <p className="muted">You are signed in as <strong>{signedInEmail}</strong>. This email must be on the employee list saved by the workspace owner.</p>
                ) : null}

                <label>
                  Your name
                  <input className="search-input" value={fullName} onChange={event => setFullName(event.target.value)} required autoComplete="name" />
                </label>

                {error && <p className="auth-error">{error}</p>}

                <button className="button primary large" disabled={busy}>
                  {busy ? <span className="blue-spinner button-spinner"/> : <>Enter employee workspace <ArrowRight size={17}/></>}
                </button>
              </form>
            ) : (
              <>
                <p>
                  {kind === 'batch'
                    ? `This shared link is available to ${batchPreview?.invited_count ?? 0} invited employee${(batchPreview?.invited_count ?? 0) === 1 ? '' : 's'}. Create or sign into Mail Percent with the same work email the workspace owner saved for you.`
                    : <>Use <strong>{legacyPreview?.invited_email}</strong> when creating or signing into your Mail Percent account as a <strong>{role}</strong>.</>
                  }
                </p>

                {error && <p className="auth-error">{error}</p>}

                <div className="auth-form">
                  <Link className="button primary large" to={`/signup?mode=employee&returnTo=${encodeURIComponent(`/join?token=${token}`)}&invite=${encodeURIComponent(token)}`}>Create account</Link>
                  <Link className="button ghost large" to={`/login?returnTo=${encodeURIComponent(`/join?token=${token}`)}`}>Sign in</Link>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <h1>Invitation unavailable</h1>
            <p className="auth-error">{error}</p>
          </>
        )}
      </main>
    </div>
  )
}
